import React, { useState } from 'react';
import { Box } from '@mui/material';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { Formik } from 'formik';
import * as yup from 'yup';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context';
import GeneralForm from './GeneralForm';
import AppInfoView from '@projectK/components/AppInfoView';
import { HandleMobileFormat } from '@projectK/ReUseFunctions';
import PropTypes from 'prop-types';

const validationSchema = yup.object({
  name: yup
    .string()
    .max(100, <IntlMessages id='validation.maxNameLength'/>)
    .required(<IntlMessages id='validation.nameRequired'/>),
  owner_name: yup
    .string()
    .max(100, <IntlMessages id='validation.ownerMaxNameLength'/>)
    .required(<IntlMessages id='validation.ownerNameRequired'/>),

  phone_number: yup
    .string()
    .matches(/^\d{3}-\d{3}-\d{4}$/, 'Please enter a valid phone number.')
    .required(<IntlMessages id='validation.phoneNumberRequired' />),
  email: yup
    .string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      'Invalid email format.',
    )
    .typeError(<IntlMessages id='validation.invalidEmail'/>)
    .required(<IntlMessages id='validation.emailRequired' />),
  site_contact_email: yup
    .string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      'Invalid email format.',
    )
    .typeError(<IntlMessages id='validation.invalidEmail'/>)
    .required(<IntlMessages id='validation.siteContactEmailRequired' />),
  site_contact_phone_number: yup
    .string()
    .matches(
      /^(\+\d{1,2}\s?)?(\()?\d{3}(\))?[-.\s]?\d{3}[-.\s]?\d{4}$/,
      'Please enter a valid phone number.',
    )
    .required(<IntlMessages id='validation.siteContactNumberRequired' />),
  theme: yup.string().nullable(),
  timezone: yup.string().nullable(),
  image: yup.string().required(<IntlMessages id='validation.imageRequired' />),
});

const General = ({ settings, fetchData }) => {//NOSONAR
  const infoViewActionsContext = useInfoViewActionsContext();
  const [isEdit, setIsEdit] = useState(false);
  const handleImageUpload = async (file, formikProps) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await jwtAxios.post(`/fileuploads`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const bgFileName = await response.data;
      const imageUrl = bgFileName.file || null;
      // Update form values with the image URL
      formikProps.setFieldValue('home_logo_url', imageUrl);
      return imageUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };
  const handleBannerUpload = async (file, formikProps) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await jwtAxios.post(`/fileuploads`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const bgFileName = await response.data;
      const bannerUrl = bgFileName.file || null;
      // Update form values with the image URL
      formikProps.setFieldValue('home_banner_url', bannerUrl);
      return bannerUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };
  const handleMobileFormat = (event, name) => {
    let inputValue = event.target.value.replace(/\D/g, ''); // Remove non-numeric characters

    // Ensure the first group of digits is not longer than 3 digits
    if (inputValue.length > 3) {
      inputValue = inputValue.slice(0, 3) + '-' + inputValue.slice(3);
    }

    // Ensure the second group of digits is not longer than 6 digits
    if (inputValue.length > 7) {
      inputValue = inputValue.slice(0, 7) + '-' + inputValue.slice(7);
    }
    // Update form field value
    event.target.value = inputValue; // Set input value to formatted value
    if (name === 'phone_number') {
      return { name: 'phone_number', value: inputValue || null };
    } else if (name === 'site_contact_phone_number') {
      return { name: 'site_contact_phone_number', value: inputValue || null };
    }
  };
  return (
    <>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Formik
          enableReinitialize={true}
          validateOnBlur={true}
          initialValues={{
            owner_name: settings?.owner_name || '',
            email: settings?.email || '',
            phone_number: HandleMobileFormat(settings.phone_number, true) || '',
            site_contact_email: settings?.site_contact_email || '',
            site_contact_phone_number:
              HandleMobileFormat(settings.site_contact_phone_number, true) ||
              '',
            home_logo_url: settings?.home_logo_url || '',
            home_banner_url: settings?.home_banner_url || '',
            mission_statement: settings?.mission_statement || '',
            description: settings?.description || '',
            store_description: settings?.store_description || '',
            theme: settings?.theme || '',
            external_locker_menu_url: settings?.external_locker_menu_url || '',
            event_cancellation_enabled:
              settings?.event_cancellation_enabled || '',
            event_cancellation_limit: settings?.event_cancellation_limit || '',
            fit_images_to_width_service_page:
              settings?.fit_images_to_width_service_page || '',
            join_email_type: settings?.join_email_type || '',
            attendance_notification: settings?.attendance_notification || '',
            login_landing_page: settings?.login_landing_page || '',
            hide_sync_schedule_buttons:
              settings?.hide_sync_schedule_buttons || '',
            timezone: settings?.timezone || 'America/Chicago',
            home_video_url: settings?.home_video_url || '',
            name: settings?.name || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, ...formikProps }) => {
            setSubmitting(true);
            // Prepare the data for submission
            const settingsArray = Object.keys(values).map((fieldName) => ({
              setting_name: 'basic',
              setting_slug: fieldName,
              setting_value:
                values[fieldName] === '' ? null : values[fieldName],
              is_active: true,
            }));
            jwtAxios
              .put(`/app-settings/basic/`, settingsArray)
              .then((response) => {
                setSubmitting(false);
                infoViewActionsContext.showMessage(
                  'General Settings Updated Successfully!',
                );
                setIsEdit(false);
                localStorage.setItem('theme', values.theme);
                fetchData();
                window.location.reload();
              })
              .catch((error) => {
                infoViewActionsContext.fetchError(error.message);
                console.error('Error updating basic settings:', error);
                setSubmitting(false);
              });
          }}
        >
          {(formikProps) => (
            <GeneralForm
              formikProps={formikProps}
              handleImageUpload={handleImageUpload}
              handleBannerUpload={handleBannerUpload}
              settings={settings}
              fetchData={fetchData}
              setFieldValue={formikProps.setFieldValue}
              touched={formikProps.touched}
              errors={formikProps.errors}
              setErrors={formikProps.setErrors}
              values={formikProps.values}
              resetForm={formikProps.resetForm}
              isEdit={isEdit}
              setIsEdit={setIsEdit}
              handleMobileFormat={handleMobileFormat}
              handleBlur={formikProps.handleBlur}
            />
          )}
        </Formik>
      </Box>
      <AppInfoView />
    </>
  );
};
General.propTypes = {
  settings: PropTypes.object,
  fetchData: PropTypes.func,
};
export default General;
General.propTypes = {
  settings: PropTypes.object,
  fetchData: PropTypes.func,
};
